/**
 * How long a conversation lasted, in words (BUILD-854, seconde passe).
 *
 * ## Pourquoi un module à part
 *
 * Une durée qui a deux orthographes finit par dire deux choses d'une même
 * conversation - c'est l'argument de `engine/duration.ts` côté serveur, où deux
 * fonctions portaient la même formule et où « une mesure à deux orthographes
 * finit par la plus laxiste ». Cette fonction est donc posée dans `lib/`, hors
 * de l'écran qui la monte, POUR ÊTRE ADOPTÉE : la page Sessions que le §12 de
 * la refonte modulaire réécrit imprimera la même grandeur, et elle reprendra
 * celle-ci plutôt que d'en écrire une seconde.
 *
 * ## Le défaut qu'elle ferme
 *
 * Le fil porte des minutes arrondies au DIXIÈME (`roundedMinutes` du journal,
 * `tenths` de la vue d'ensemble), donc une conversation de deux secondes arrive
 * à `0` et s'imprimait « 0 min » - à côté d'une session de dix-neuf tours. Or
 * `0` et `null` ne disent pas du tout la même chose : `null` est « personne ne
 * l'a mesurée », `0` est « elle a été mesurée et elle est très courte ».
 * `engine/duration.ts` le dit en toutes lettres à l'autre bout du fil - « Null
 * means "no duration", never zero: a zero would enter the H2 median and pull it
 * down in silence » - et l'affichage écrasait justement cette distinction.
 *
 * ## Ce qu'elle N'INVENTE PAS
 *
 * Elle ne fabrique aucune valeur : `durationLabel` ne prend qu'un nombre, donc
 * l'ABSENCE reste l'affaire de l'appelant et ne peut pas se déguiser en zéro
 * (`missingDuration` lui donne son mot, une fois, pour que les appelants ne
 * l'écrivent pas chacun de leur côté). Et elle ne descend pas en secondes sous
 * la minute : à un dixième de minute près, « 24 s » afficherait une précision à
 * la seconde que le fil ne porte pas, et « 0 s » recréerait exactement le piège
 * qu'on ferme. « < 1 min » est vrai de toute valeur strictement inférieure à
 * une minute, zéro compris, et ne prétend rien de plus.
 */

/** Ce qu'imprime une durée mesurée mais inférieure à la minute. */
export const SHORT_DURATION = '< 1 min';

/**
 * Ce qu'imprime une durée ABSENTE.
 *
 * Un tiret, et jamais un zéro : c'est la seule chose qui distingue « on ne l'a
 * pas mesurée » de « elle a duré moins d'une minute », et c'est toute la
 * correction. Les appelants qui ont la place nomment l'absence en toutes lettres
 * à côté (la carte de durée du tableau de bord porte « aucune conversation
 * mesurée sur la période » sous son chiffre).
 */
export const MISSING_DURATION = '-';

/** Les décimales à la française : un point est une faute dans une phrase. */
function frenchDecimal(value: number): string {
  return value.toString().replace('.', ',');
}

/**
 * Une durée en minutes, avec son unité épelée - un nombre nu n'est pas une
 * durée.
 *
 * Sous la minute elle rend `SHORT_DURATION` plutôt qu'un décimal : « 0,4 min »
 * était honnête et illisible, « 0 min » était faux.
 */
export function durationLabel(minutes: number): string {
  if (!Number.isFinite(minutes)) return MISSING_DURATION;
  if (minutes < 1) return SHORT_DURATION;
  return `${frenchDecimal(minutes)} min`;
}

/** La même, tolérante à l'absence : `null` rend le tiret et jamais un zéro. */
export function durationLabelOrNothing(minutes: number | null): string {
  return minutes === null ? MISSING_DURATION : durationLabel(minutes);
}
